import React from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';


const MaintenancePage = ({ message, estimatedReturn }) => {
  const displayMessage = message || "We are currently performing scheduled maintenance to improve your experience. We'll be back shortly.";

  return (
    <>
      <Helmet>
        <title>Maintenance - MyBodyToday™</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col items-center justify-center px-4">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-lg w-full text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Icon name="Activity" size={24} className="text-white" />
              </div>
              <div className="text-left">
                <span className="text-white font-bold text-xl tracking-tight">MyBodyToday</span>
                <sup className="text-blue-400 text-xs font-semibold ml-0.5">™</sup>
              </div>
            </div>
          </div>

          {/* Main card */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
            {/* Maintenance icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center border border-amber-500/30">
                  <Icon name="Wrench" size={36} className="text-amber-400" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                  <Icon name="Zap" size={12} className="text-white" />
                </div>
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-3xl font-bold text-white mb-2">
              We'll be back soon
            </h1>
            <p className="text-blue-300 text-sm font-medium mb-6 uppercase tracking-widest">
              Scheduled Maintenance
            </p>

            {/* Divider */}
            <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto mb-6" />

            {/* Message */}
            <p className="text-slate-300 text-base leading-relaxed mb-6">
              {displayMessage}
            </p>

            {/* Estimated return time */}
            {estimatedReturn && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-blue-300">
                  <Icon name="Clock" size={16} />
                  <span className="text-sm font-medium">Estimated return:</span>
                </div>
                <p className="text-white font-semibold mt-1">{estimatedReturn}</p>
              </div>
            )}

            {/* What we're doing */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { icon: 'Shield', label: 'Security Updates' },
                { icon: 'Zap', label: 'Performance' },
                { icon: 'Star', label: 'New Features' }
              ]?.map((item) => (
                <div key={item?.label} className="bg-white/5 rounded-lg p-3 border border-white/5">
                  <Icon name={item?.icon} size={18} className="text-blue-400 mx-auto mb-1" />
                  <p className="text-slate-400 text-xs">{item?.label}</p>
                </div>
              ))}
            </div>

            {/* Contact support */}
            <div className="border-t border-white/10 pt-5">
              <p className="text-slate-400 text-sm mb-3">Need urgent assistance?</p>
              <a
                href="mailto:support@mybodytoday1.com"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors duration-200"
              >
                <Icon name="Mail" size={15} />
                support@mybodytoday1.com
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-xs">
            <Icon name="Lock" size={12} />
            <span>Your data is safe and secure during maintenance</span>
          </div>

          <p className="mt-3 text-slate-600 text-xs">
            © {new Date()?.getFullYear()} MyBodyToday™. All rights reserved.
          </p>
        </div>
      </div>
    </>
  );
};

export default MaintenancePage;
